import { type ReactNode, useState } from 'react';
import { Button, CircularProgress, type ButtonProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toError } from '@/shared/lib/toError';
import { useCreatePaymentMethodPortalSession } from '../api/useCreatePaymentMethodPortalSession';

type AddPaymentMethodButtonProps = ButtonProps<
  'button',
  {
    onSuccess?: (portalUrl: string) => void;
    onMutationError?: (error: Error) => void;
    children?: ReactNode;
  }
>;

export const AddPaymentMethodButton = ({
  onSuccess,
  onMutationError,
  children,
  disabled,
  startIcon,
  ...buttonProps
}: AddPaymentMethodButtonProps) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useCreatePaymentMethodPortalSession();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleClick = () => {
    mutate(
      { data: { flow: 'payment_method_update' } },
      {
        onSuccess: (data) => {
          setIsRedirecting(true);
          onSuccess?.(data.portalUrl);
          window.open(data.portalUrl, '_blank');
          setIsRedirecting(false);
        },
        onError: (error) => {
          onMutationError?.(toError(error));
        },
      }
    );
  };

  const isLoading = isPending || isRedirecting;

  return (
    <Button
      {...buttonProps}
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : startIcon}
    >
      {children ?? t('subscription.usage.addPaymentMethod')}
    </Button>
  );
};
